import * as THREE from "three";
import type { SceneVariant } from "./themes.ts";

/** Collects everything that owns GPU memory so a scene can be torn down in one call. */
export class Bag {
  private items: { dispose(): void }[] = [];
  add<T extends { dispose(): void }>(item: T): T { this.items.push(item); return item; }
  dispose() { for (const i of this.items) i.dispose(); this.items.length = 0; }
}

export interface SceneRig {
  /** Camera resting position and look target; the host adds pointer parallax on top. */
  pos: THREE.Vector3;
  look: THREE.Vector3;
  /** How strongly the scene moves off-centre on wide screens (default 1). */
  shift?: number;
  update(t: number, dt: number): void;
}

export interface BuildCtx { root: THREE.Group; bag: Bag; a: THREE.Color; b: THREE.Color }
export type Builder = (ctx: BuildCtx) => SceneRig;

const TAU = Math.PI * 2;
const WHITE = new THREE.Color(1, 1, 1);
const glow = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending } as const;
const at = (arr: ArrayLike<number>, i: number) => arr[i]!;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function glowTexture(bag: Bag) {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.25, "rgba(255,255,255,0.5)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return bag.add(tex);
}

const lineMat = (bag: Bag, color: THREE.ColorRepresentation, opacity = 1) =>
  bag.add(new THREE.LineBasicMaterial({ color, opacity, ...glow }));

function pointsMat(bag: Bag, map: THREE.Texture, size: number) {
  return bag.add(new THREE.PointsMaterial({ map, size, vertexColors: true, sizeAttenuation: true, ...glow }));
}

/** A circle in the XY plane. */
function circleGeo(bag: Bag, r: number, seg = 96) {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i < seg; i++) pts.push(new THREE.Vector3(Math.cos((i / seg) * TAU) * r, Math.sin((i / seg) * TAU) * r, 0));
  return bag.add(new THREE.BufferGeometry().setFromPoints(pts));
}

/** Points with per-frame positions and colours. Culling is off because the bounds are never recomputed. */
function cloud(bag: Bag, n: number, map: THREE.Texture, size: number) {
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const geo = bag.add(new THREE.BufferGeometry());
  const pa = new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage);
  const ca = new THREE.BufferAttribute(col, 3).setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute("position", pa);
  geo.setAttribute("color", ca);
  const points = new THREE.Points(geo, pointsMat(bag, map, size));
  points.frustumCulled = false;
  return { points, pos, col, touch() { pa.needsUpdate = true; ca.needsUpdate = true; } };
}

/** Static point shell with dim random colours. */
function dust(bag: Bag, map: THREE.Texture, n: number, size: number, a: THREE.Color, b: THREE.Color, place: (rand: () => number) => [number, number, number], seed: number) {
  const rand = rng(seed);
  const c = cloud(bag, n, map, size);
  const tmp = new THREE.Color();
  for (let i = 0; i < n; i++) {
    const [x, y, z] = place(rand);
    c.pos[i * 3] = x; c.pos[i * 3 + 1] = y; c.pos[i * 3 + 2] = z;
    tmp.copy(a).lerp(b, rand()).multiplyScalar(0.25 + rand() * 0.6);
    c.col[i * 3] = tmp.r; c.col[i * 3 + 1] = tmp.g; c.col[i * 3 + 2] = tmp.b;
  }
  c.touch();
  return c.points;
}

// ── voxel: floating island with XP orbs ──

const voxel: Builder = ({ root, bag, a, b }) => {
  const rand = rng(11);
  const pick = (arr: number[]) => arr[Math.floor(rand() * arr.length)]!;
  const GRASS = [0x5fb946, 0x56ad3f, 0x6cc953], DIRT = [0x8b5a2b, 0x7d5026, 0x6e4623], STONE = [0x7d7f86, 0x6c6e75];
  type Cube = { x: number; y: number; z: number; c: THREE.Color };
  const cubes: Cube[] = [];
  const tops: { x: number; z: number }[] = [];
  const R = 6;
  for (let x = -R; x <= R; x++) for (let z = -R; z <= R; z++) {
    const d = Math.hypot(x, z) + (rand() - 0.5) * 1.8;
    if (d > R) continue;
    const depth = Math.min(6, Math.max(1, Math.round((R - d) * 0.8)));
    for (let k = 0; k < depth; k++) cubes.push({ x, y: -k, z, c: new THREE.Color(k === 0 ? pick(GRASS) : k < 3 ? pick(DIRT) : pick(STONE)) });
    if (d < 4.2) tops.push({ x, z });
  }
  // A few trees, kept apart.
  const trees: { x: number; z: number }[] = [];
  for (let tries = 0; tries < 60 && trees.length < 3; tries++) {
    const p = tops[Math.floor(rand() * tops.length)]!;
    if (trees.every((q) => Math.hypot(q.x - p.x, q.z - p.z) >= 3.5)) trees.push(p);
  }
  for (const t of trees) {
    for (let y = 1; y <= 3; y++) cubes.push({ x: t.x, y, z: t.z, c: new THREE.Color(0x6b4a2b) });
    for (let y = 3; y <= 4; y++) for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) {
      if (y === 4 && dx !== 0 && dz !== 0) continue;
      if (y === 3 && dx === 0 && dz === 0) continue;
      cubes.push({ x: t.x + dx, y, z: t.z + dz, c: new THREE.Color(rand() < 0.5 ? 0x3f8f35 : 0x4aa23c) });
    }
    cubes.push({ x: t.x, y: 5, z: t.z, c: new THREE.Color(0x4aa23c) });
  }
  // Glowing ore in the deep stone, tinted with the game accent.
  const deep = cubes.filter((c) => c.y <= -3);
  for (let i = 0; i < 5 && deep.length; i++) deep[Math.floor(rand() * deep.length)]!.c.copy(a);

  const geo = bag.add(new THREE.BoxGeometry(1, 1, 1));
  const mat = bag.add(new THREE.MeshLambertMaterial({ color: 0xffffff }));
  const mesh = bag.add(new THREE.InstancedMesh(geo, mat, cubes.length));
  const m = new THREE.Object3D();
  m.scale.setScalar(0.97);
  cubes.forEach((c, i) => {
    m.position.set(c.x, c.y, c.z);
    m.updateMatrix();
    mesh.setMatrixAt(i, m.matrix);
    mesh.setColorAt(i, c.c.multiplyScalar(0.88 + rand() * 0.2));
  });
  const island = new THREE.Group();
  island.add(mesh);
  island.rotation.x = 0.1;
  root.add(island);
  root.add(new THREE.AmbientLight(0xffffff, 0.9));
  const sun = new THREE.DirectionalLight(0xffffff, 2.2);
  sun.position.set(5, 9, 6);
  root.add(sun);
  const under = new THREE.PointLight(a, 60, 22, 2);
  under.position.set(0, -6, 3);
  root.add(under);

  const N = 90;
  const orbs = cloud(bag, N, glowTexture(bag), 0.5);
  const orbCol = new THREE.Color(0xc6ff3d).lerp(a, 0.25);
  const ang = new Float32Array(N), rad = new Float32Array(N), spd = new Float32Array(N), ph = new Float32Array(N);
  for (let i = 0; i < N; i++) { ang[i] = rand() * TAU; rad[i] = 3 + rand() * 5.5; spd[i] = 0.05 + rand() * 0.07; ph[i] = rand(); }
  root.add(orbs.points);

  return {
    pos: new THREE.Vector3(0, 5, 27),
    look: new THREE.Vector3(0, -1.6, 0),
    update(t) {
      island.rotation.y = t * 0.18;
      island.position.y = Math.sin(t * 0.7) * 0.3;
      for (let i = 0; i < N; i++) {
        const life = (t * at(spd, i) + at(ph, i)) % 1;
        const a2 = at(ang, i) + t * 0.35;
        const r = at(rad, i) * (0.85 + 0.15 * Math.sin(life * TAU));
        orbs.pos[i * 3] = Math.cos(a2) * r;
        orbs.pos[i * 3 + 1] = -4 + life * 11;
        orbs.pos[i * 3 + 2] = Math.sin(a2) * r;
        const k = Math.pow(Math.sin(Math.PI * life), 0.7);
        orbs.col[i * 3] = orbCol.r * k; orbs.col[i * 3 + 1] = orbCol.g * k; orbs.col[i * 3 + 2] = orbCol.b * k;
      }
      orbs.touch();
    },
  };
};

// ── tracers: streaks converging on a reticle ──

const tracers: Builder = ({ root, bag, a, b }) => {
  const rand = rng(5);
  const tex = glowTexture(bag);
  const N = 150;
  const dir = new Float32Array(N * 3), r = new Float32Array(N), speed = new Float32Array(N), len = new Float32Array(N);
  const linePos = new Float32Array(N * 6), lineCol = new Float32Array(N * 6);
  const heads = cloud(bag, N, tex, 0.32);
  const c = new THREE.Color();
  for (let i = 0; i < N; i++) {
    const th = rand() * TAU, z = (rand() - 0.5) * 0.9, n = Math.hypot(1, z);
    dir[i * 3] = Math.cos(th) / n; dir[i * 3 + 1] = Math.sin(th) / n; dir[i * 3 + 2] = z / n;
    r[i] = 1.5 + rand() * 22; speed[i] = 5 + rand() * 9; len[i] = 1.2 + rand() * 3.2;
    c.copy(rand() < 0.65 ? a : b).lerp(WHITE, 0.2);
    lineCol[i * 6] = c.r; lineCol[i * 6 + 1] = c.g; lineCol[i * 6 + 2] = c.b; // tail stays black = invisible when additive
    heads.col[i * 3] = c.r; heads.col[i * 3 + 1] = c.g; heads.col[i * 3 + 2] = c.b;
  }
  const lineGeo = bag.add(new THREE.BufferGeometry());
  const lp = new THREE.BufferAttribute(linePos, 3).setUsage(THREE.DynamicDrawUsage);
  lineGeo.setAttribute("position", lp);
  lineGeo.setAttribute("color", new THREE.BufferAttribute(lineCol, 3));
  const lines = new THREE.LineSegments(lineGeo, bag.add(new THREE.LineBasicMaterial({ vertexColors: true, opacity: 0.9, ...glow })));
  lines.frustumCulled = false;
  root.add(lines, heads.points);

  // Reticle
  const reticle = new THREE.Group();
  root.add(reticle);
  const ring = new THREE.LineLoop(circleGeo(bag, 1.25), lineMat(bag, a, 0.95));
  const dashed = new THREE.LineLoop(circleGeo(bag, 1.9), bag.add(new THREE.LineDashedMaterial({ color: b, dashSize: 0.16, gapSize: 0.12, opacity: 0.8, ...glow })));
  dashed.computeLineDistances();
  const tickPts: number[] = [];
  for (let k = 0; k < 4; k++) {
    const an = (k * Math.PI) / 2;
    tickPts.push(Math.cos(an) * 1.0, Math.sin(an) * 1.0, 0, Math.cos(an) * 1.7, Math.sin(an) * 1.7, 0);
  }
  const tickGeo = bag.add(new THREE.BufferGeometry());
  tickGeo.setAttribute("position", new THREE.Float32BufferAttribute(tickPts, 3));
  const ticks = new THREE.LineSegments(tickGeo, lineMat(bag, WHITE, 0.8));
  const icoSrc = bag.add(new THREE.IcosahedronGeometry(0.55, 0));
  const icoEdges = bag.add(new THREE.EdgesGeometry(icoSrc));
  const ico = new THREE.LineSegments(icoEdges, lineMat(bag, b, 1));
  const halo = new THREE.Sprite(bag.add(new THREE.SpriteMaterial({ map: tex, color: a, opacity: 0.7, ...glow })));
  halo.scale.setScalar(3.2);
  reticle.add(ring, dashed, ticks, ico, halo);

  root.add(dust(bag, tex, 260, 0.1, a, b, (rn) => [(rn() - 0.5) * 40, (rn() - 0.5) * 24, (rn() - 0.5) * 16 - 4], 9));

  return {
    pos: new THREE.Vector3(0, 0, 15),
    look: new THREE.Vector3(0, 0, 0),
    update(t, dt) {
      for (let i = 0; i < N; i++) {
        let ri = at(r, i) - at(speed, i) * dt;
        if (ri < 1.5) ri = 19 + rand() * 7;
        r[i] = ri;
        const dx = at(dir, i * 3), dy = at(dir, i * 3 + 1), dz = at(dir, i * 3 + 2), tl = ri + at(len, i), o = i * 6;
        linePos[o] = dx * ri; linePos[o + 1] = dy * ri; linePos[o + 2] = dz * ri;
        linePos[o + 3] = dx * tl; linePos[o + 4] = dy * tl; linePos[o + 5] = dz * tl;
        heads.pos[i * 3] = dx * ri; heads.pos[i * 3 + 1] = dy * ri; heads.pos[i * 3 + 2] = dz * ri;
      }
      lp.needsUpdate = true;
      heads.touch();
      reticle.rotation.z = t * 0.25;
      dashed.rotation.z = -t * 0.7;
      ico.rotation.x = t * 0.6; ico.rotation.y = t * 0.9;
      halo.material.opacity = 0.55 + 0.2 * Math.sin(t * 3);
    },
  };
};

// ── terrain: rolling wireframe landscape with rising embers ──

const terrain: Builder = ({ root, bag, a, b }) => {
  const rand = rng(21);
  const tex = glowTexture(bag);
  const COLS = 56, ROWS = 48, W = 52, Z0 = 8, Z1 = -46;
  const n = COLS * ROWS;
  const pos = new Float32Array(n * 3), col = new Float32Array(n * 3), fade = new Float32Array(n);
  for (let i = 0; i < ROWS; i++) for (let j = 0; j < COLS; j++) {
    const idx = i * COLS + j, f = i / (ROWS - 1);
    pos[idx * 3] = (j / (COLS - 1) - 0.5) * W;
    pos[idx * 3 + 2] = Z0 + (Z1 - Z0) * f;
    const s = clamp01(f / 0.08);
    fade[idx] = Math.pow(1 - f, 1.6) * s * s * (3 - 2 * s);
  }
  const indices: number[] = [];
  for (let i = 0; i < ROWS; i++) for (let j = 0; j < COLS; j++) {
    const idx = i * COLS + j;
    if (j < COLS - 1) indices.push(idx, idx + 1);
    if (i < ROWS - 1) indices.push(idx, idx + COLS);
  }
  const geo = bag.add(new THREE.BufferGeometry());
  const pa = new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage);
  const ca = new THREE.BufferAttribute(col, 3).setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute("position", pa);
  geo.setAttribute("color", ca);
  geo.setIndex(indices);
  const land = new THREE.LineSegments(geo, bag.add(new THREE.LineBasicMaterial({ vertexColors: true, opacity: 0.95, ...glow })));
  land.frustumCulled = false;
  root.add(land);

  const edge = (x: number) => { const u = clamp01((Math.abs(x) - 3) / 12); return u * u * (3 - 2 * u); };
  const c = new THREE.Color();

  const E = 140;
  const embers = cloud(bag, E, tex, 0.34);
  const ex = new Float32Array(E), ez = new Float32Array(E), es = new Float32Array(E), ep = new Float32Array(E);
  for (let i = 0; i < E; i++) { ex[i] = (rand() - 0.5) * 40; ez[i] = -34 + rand() * 39; es[i] = 0.05 + rand() * 0.09; ep[i] = rand(); }
  const emberCol = new THREE.Color(b).lerp(WHITE, 0.15);
  root.add(embers.points);

  // Pale moon on the horizon.
  const moonHalo = new THREE.Sprite(bag.add(new THREE.SpriteMaterial({ map: tex, color: b, opacity: 0.35, ...glow })));
  moonHalo.scale.setScalar(40);
  moonHalo.position.set(8, 9, -48);
  const moon = new THREE.Mesh(bag.add(new THREE.RingGeometry(4.7, 5, 72)), bag.add(new THREE.MeshBasicMaterial({ color: b, opacity: 0.6, ...glow })));
  moon.position.copy(moonHalo.position);
  root.add(moonHalo, moon);

  return {
    pos: new THREE.Vector3(0, 3.4, 10),
    look: new THREE.Vector3(0, 1.2, -14),
    shift: 0.2,
    update(t) {
      for (let i = 0; i < ROWS; i++) for (let j = 0; j < COLS; j++) {
        const idx = i * COLS + j;
        const x = at(pos, idx * 3), zz = at(pos, idx * 3 + 2) - t * 3.2;
        const h = edge(x) * (Math.sin(x * 0.31 + zz * 0.11) * 2.4 + Math.sin(x * 0.13 - zz * 0.19) * 3.2 + Math.sin(zz * 0.47 + x * 0.23) * 0.7) + Math.sin(zz * 0.35 + x * 0.9) * 0.18;
        pos[idx * 3 + 1] = h - 1.6;
        const hn = clamp01((h + 1) / 6);
        const k = at(fade, idx) * (0.28 + 0.9 * hn);
        c.copy(a).lerp(b, hn);
        col[idx * 3] = c.r * k; col[idx * 3 + 1] = c.g * k; col[idx * 3 + 2] = c.b * k;
      }
      pa.needsUpdate = true; ca.needsUpdate = true;
      for (let i = 0; i < E; i++) {
        const life = (t * at(es, i) + at(ep, i)) % 1;
        embers.pos[i * 3] = at(ex, i) + Math.sin(t * 0.5 + at(ep, i) * TAU) * 0.8;
        embers.pos[i * 3 + 1] = -1.4 + life * 10;
        embers.pos[i * 3 + 2] = at(ez, i);
        const k = Math.sin(Math.PI * life) * (0.7 + 0.3 * Math.sin(t * 9 + at(ep, i) * 40));
        embers.col[i * 3] = emberCol.r * k; embers.col[i * 3 + 1] = emberCol.g * k; embers.col[i * 3 + 2] = emberCol.b * k;
      }
      embers.touch();
    },
  };
};

// ── hero: core, orbiting rings and a hex shield ──

const hero: Builder = ({ root, bag, a, b }) => {
  const tex = glowTexture(bag);

  const core = new THREE.Group();
  const wire = new THREE.Mesh(bag.add(new THREE.IcosahedronGeometry(0.85, 1)), bag.add(new THREE.MeshBasicMaterial({ color: b, wireframe: true, opacity: 0.9, ...glow })));
  const inner = new THREE.Mesh(bag.add(new THREE.IcosahedronGeometry(0.5, 0)), bag.add(new THREE.MeshBasicMaterial({ color: a, opacity: 0.85, ...glow })));
  const halo = new THREE.Sprite(bag.add(new THREE.SpriteMaterial({ map: tex, color: a, opacity: 0.75, ...glow })));
  halo.scale.setScalar(6);
  core.add(wire, inner, halo);
  root.add(core);

  const beadGeo = bag.add(new THREE.SphereGeometry(0.07, 12, 8));
  const beadMat = bag.add(new THREE.MeshBasicMaterial({ color: 0xffffff }));
  const ringMat = bag.add(new THREE.MeshBasicMaterial({ color: a, opacity: 0.7, ...glow }));
  const spinners: { g: THREE.Group; speed: number }[] = [];
  for (const d of [{ R: 1.9, tx: 1.1, ty: 0.2, speed: 0.7 }, { R: 2.6, tx: 0.4, ty: 0.9, speed: -0.5 }, { R: 3.2, tx: -0.6, ty: 0.3, speed: 0.35 }]) {
    const tilt = new THREE.Group();
    tilt.rotation.set(d.tx, d.ty, 0);
    const spin = new THREE.Group();
    spin.add(new THREE.Mesh(bag.add(new THREE.TorusGeometry(d.R, 0.018, 8, 128)), ringMat));
    const bead = new THREE.Mesh(beadGeo, beadMat);
    bead.position.x = d.R;
    spin.add(bead);
    tilt.add(spin);
    root.add(tilt);
    spinners.push({ g: spin, speed: d.speed });
  }

  // Shield: hex panels spread over a sphere (Fibonacci lattice), facing the core.
  const P = 72, SR = 3.9;
  const shield = new THREE.Group();
  root.add(shield);
  const hexFill = bag.add(new THREE.InstancedMesh(bag.add(new THREE.CircleGeometry(1, 6)), bag.add(new THREE.MeshBasicMaterial({ color: b, opacity: 0.06, side: THREE.DoubleSide, ...glow })), P));
  const hexLine = bag.add(new THREE.InstancedMesh(bag.add(new THREE.RingGeometry(0.88, 1, 6)), bag.add(new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0.9, side: THREE.DoubleSide, ...glow })), P));
  const dummy = new THREE.Object3D();
  const px = new Float32Array(P), py = new Float32Array(P);
  const tmp = new THREE.Color();
  for (let i = 0; i < P; i++) {
    const y = 1 - ((i + 0.5) / P) * 2, rr = Math.sqrt(1 - y * y), phi = i * 2.399963;
    dummy.position.set(Math.cos(phi) * rr * SR, y * SR, Math.sin(phi) * rr * SR);
    dummy.scale.setScalar(0.82);
    dummy.lookAt(0, 0, 0);
    dummy.updateMatrix();
    hexFill.setMatrixAt(i, dummy.matrix);
    hexLine.setMatrixAt(i, dummy.matrix);
    hexLine.setColorAt(i, tmp.copy(a));
    px[i] = dummy.position.x; py[i] = dummy.position.y;
  }
  shield.add(hexFill, hexLine);

  const orbit = new THREE.Group();
  orbit.add(dust(bag, tex, 160, 0.14, a, b, (rn) => {
    const an = rn() * TAU, rd = 2.5 + rn() * 3, y = (rn() - 0.5) * 1.6;
    return [Math.cos(an) * rd, y, Math.sin(an) * rd];
  }, 41));
  root.add(orbit);

  return {
    pos: new THREE.Vector3(0, 0.5, 14.5),
    look: new THREE.Vector3(0, 0, 0),
    update(t) {
      core.rotation.y = t * 0.8; core.rotation.x = t * 0.4;
      inner.scale.setScalar(1 + 0.08 * Math.sin(t * 2.4));
      for (const s of spinners) s.g.rotation.z = t * s.speed;
      shield.rotation.y = t * 0.12;
      shield.rotation.x = Math.sin(t * 0.3) * 0.15;
      orbit.rotation.y = -t * 0.2;
      for (let i = 0; i < P; i++) {
        const v = 0.5 + 0.5 * Math.sin(t * 1.7 - at(py, i) * 0.8 + at(px, i) * 0.3);
        const k = 0.12 + 0.88 * Math.pow(v, 4);
        tmp.copy(a).lerp(WHITE, Math.pow(v, 6) * 0.6).multiplyScalar(k);
        hexLine.setColorAt(i, tmp);
      }
      hexLine.instanceColor!.needsUpdate = true;
    },
  };
};

// ── radar: sweep, blips and fighter silhouettes ──

const radar: Builder = ({ root, bag, a, b }) => {
  const rand = rng(8);
  const tex = glowTexture(bag);
  const R = 5, SPEED = 1.1;
  const disc = new THREE.Group();
  root.add(disc);

  const base = new THREE.Mesh(bag.add(new THREE.CircleGeometry(R, 64)), bag.add(new THREE.MeshBasicMaterial({ color: a, opacity: 0.05, side: THREE.DoubleSide, ...glow })));
  base.rotation.x = -Math.PI / 2;
  disc.add(base);
  for (let k = 1; k <= 5; k++) {
    const ring = new THREE.LineLoop(circleGeo(bag, k), lineMat(bag, a, k === 5 ? 0.9 : 0.3));
    ring.rotation.x = -Math.PI / 2;
    disc.add(ring);
  }
  const spokes: number[] = [];
  for (let k = 0; k < 12; k++) { const an = (k / 12) * TAU; spokes.push(0, 0, 0, Math.cos(an) * R, 0, Math.sin(an) * R); }
  const spokeGeo = bag.add(new THREE.BufferGeometry());
  spokeGeo.setAttribute("position", new THREE.Float32BufferAttribute(spokes, 3));
  disc.add(new THREE.LineSegments(spokeGeo, lineMat(bag, a, 0.18)));

  // Sweep wedge: leading edge at angle 0, fading toward the trailing edge.
  const S = 36, span = 1.3;
  const fanPos = [0, 0, 0], fanCol = [0, 0, 0], fanIdx: number[] = [];
  for (let k = 0; k <= S; k++) {
    const th = (k / S) * span, br = Math.pow(1 - k / S, 2) * 0.7;
    fanPos.push(Math.cos(th) * R, 0, Math.sin(th) * R);
    fanCol.push(a.r * br, a.g * br, a.b * br);
    if (k < S) fanIdx.push(0, k + 1, k + 2);
  }
  const fanGeo = bag.add(new THREE.BufferGeometry());
  fanGeo.setAttribute("position", new THREE.Float32BufferAttribute(fanPos, 3));
  fanGeo.setAttribute("color", new THREE.Float32BufferAttribute(fanCol, 3));
  fanGeo.setIndex(fanIdx);
  const sweep = new THREE.Group();
  sweep.add(new THREE.Mesh(fanGeo, bag.add(new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, ...glow }))));
  const edgeGeo = bag.add(new THREE.BufferGeometry());
  edgeGeo.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0, R, 0, 0], 3));
  sweep.add(new THREE.LineSegments(edgeGeo, lineMat(bag, b, 1)));
  disc.add(sweep);

  // Blips light up as the sweep passes over them.
  const M = 16;
  const blips = cloud(bag, M, tex, 0.6);
  const ba = new Float32Array(M), hostile = new Uint8Array(M);
  for (let i = 0; i < M; i++) {
    const an = rand() * TAU, rd = Math.sqrt(rand()) * (R - 0.4);
    ba[i] = an; hostile[i] = rand() < 0.3 ? 1 : 0;
    blips.pos[i * 3] = Math.cos(an) * rd; blips.pos[i * 3 + 1] = 0.04; blips.pos[i * 3 + 2] = Math.sin(an) * rd;
  }
  disc.add(blips.points);

  // Fighter silhouettes on low orbits, each with a drop line to the scope.
  const planePts: number[] = [];
  const seg = (x1: number, y1: number, z1: number, x2: number, y2: number, z2: number) => {
    planePts.push(x1, y1, z1, x2, y2, z2);
    if (x1 !== 0 || x2 !== 0) planePts.push(-x1, y1, z1, -x2, y2, z2);
  };
  seg(0, 0, -1, 0, 0, 0.85);
  seg(0, 0, -0.3, 0.95, 0, 0.45); seg(0.95, 0, 0.45, 0.7, 0, 0.55); seg(0.7, 0, 0.55, 0, 0, 0.3);
  seg(0, 0, 0.6, 0.4, 0, 0.9); seg(0.4, 0, 0.9, 0, 0, 0.85);
  seg(0, 0, 0.5, 0, 0.35, 0.9); seg(0, 0.35, 0.9, 0, 0, 0.9);
  const planeGeo = bag.add(new THREE.BufferGeometry());
  planeGeo.setAttribute("position", new THREE.Float32BufferAttribute(planePts, 3));
  const planeMat = lineMat(bag, b, 0.95);
  const dropMat = lineMat(bag, b, 0.25);
  const holders: { g: THREE.Group; w: number }[] = [];
  for (const d of [{ R: 2.0, h: 1.3, w: 0.55 }, { R: 3.3, h: 1.9, w: -0.4 }, { R: 4.2, h: 1.1, w: 0.3 }]) {
    const holder = new THREE.Group();
    const plane = new THREE.LineSegments(planeGeo, planeMat);
    plane.position.set(d.R, d.h, 0);
    plane.scale.setScalar(0.75);
    plane.rotation.set(0, d.w < 0 ? Math.PI : 0, d.w < 0 ? -0.3 : 0.3);
    const dropGeo = bag.add(new THREE.BufferGeometry());
    dropGeo.setAttribute("position", new THREE.Float32BufferAttribute([d.R, 0, 0, d.R, d.h, 0], 3));
    holder.add(plane, new THREE.LineSegments(dropGeo, dropMat));
    disc.add(holder);
    holders.push({ g: holder, w: d.w });
  }

  return {
    pos: new THREE.Vector3(0, 6.2, 8),
    look: new THREE.Vector3(0, 0.3, 0),
    update(t) {
      sweep.rotation.y = t * SPEED;
      const psi = -t * SPEED;
      for (let i = 0; i < M; i++) {
        const d = (((at(ba, i) - psi) % TAU) + TAU) % TAU;
        const k = 0.07 + 0.93 * Math.exp(-d * 0.8);
        const c = at(hostile, i) ? b : a;
        blips.col[i * 3] = c.r * k; blips.col[i * 3 + 1] = c.g * k; blips.col[i * 3 + 2] = c.b * k;
      }
      blips.touch();
      for (const h of holders) h.g.rotation.y = t * h.w;
    },
  };
};

// ── default: particle field around a wireframe core ──

const fallback: Builder = ({ root, bag, a, b }) => {
  const tex = glowTexture(bag);
  const field = dust(bag, tex, 700, 0.14, a, b, (rn) => {
    const u = rn() * 2 - 1, an = rn() * TAU, rr = Math.sqrt(1 - u * u), rd = 3 + Math.pow(rn(), 0.7) * 6;
    return [Math.cos(an) * rr * rd, u * rd, Math.sin(an) * rr * rd];
  }, 3);
  const outer = new THREE.Mesh(bag.add(new THREE.IcosahedronGeometry(1.6, 1)), bag.add(new THREE.MeshBasicMaterial({ color: a, wireframe: true, opacity: 0.8, ...glow })));
  const innerM = new THREE.Mesh(bag.add(new THREE.OctahedronGeometry(0.9, 0)), bag.add(new THREE.MeshBasicMaterial({ color: b, wireframe: true, opacity: 0.9, ...glow })));
  const halo = new THREE.Sprite(bag.add(new THREE.SpriteMaterial({ map: tex, color: a, opacity: 0.5, ...glow })));
  halo.scale.setScalar(6);
  root.add(field, outer, innerM, halo);
  return {
    pos: new THREE.Vector3(0, 0, 14),
    look: new THREE.Vector3(0, 0, 0),
    update(t) {
      field.rotation.y = t * 0.05; field.rotation.x = t * 0.02;
      outer.rotation.set(t * 0.2, t * 0.3, 0);
      innerM.rotation.set(-t * 0.4, -t * 0.25, 0);
    },
  };
};

export const BUILDERS: Record<SceneVariant, Builder> = { voxel, tracers, terrain, hero, radar, default: fallback };
